import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Ban, Banknote, Boxes, Calendar, CheckCircle2, ChevronRight, ClipboardCheck, Clock,
  Gift, IdCard, Info, Landmark, Map as MapIcon, Package, Percent, Phone, Receipt, ShieldAlert, ShoppingCart,
  Truck, Undo2, UserCheck, Wallet, Wrench, Hourglass,
} from 'lucide-react';
import type { ReactNode } from 'react';
import type { WorkCategory, WorkItem } from '../hooks/useWorklist';
import { inr } from '../lib/format';
import { cn } from '../ui/cn';

/**
 * The rows of "My Work", shared by the login greeting and the /my-work page so the two can never
 * disagree about how a piece of work is described or where it goes.
 *
 * WRITTEN FOR SOMEONE WHO DOES NOT USE COMPUTERS MUCH. That constraint, not house style, decides
 * the layout:
 *
 *  · The WHOLE row is the button — anywhere the finger lands, opens the thing.
 *  · Three anchors, always in the same places: a coloured SUBJECT tile on the left, the sentence in
 *    the middle, the NUMBER and the MONEY on the right. A reader learns the shape once.
 *  · Two lines of words: what to do, then what happens if you don't. The second line is what turns
 *    "3 orders" into a reason to press.
 *  · Waiting time is a pill saying "34 days", never a date the reader must subtract from today.
 *  · Nothing truncates with an ellipsis. Sentences wrap.
 *
 * WHY THE TILE IS COLOURED BY SUBJECT AND NOT BY URGENCY. The first cut tinted everything by
 * urgency, and because the list sorts oldest-first, the top five rows were always five identical
 * red circles — a wall with no shape, where nothing stood out because everything was shouting.
 * Tinting by subject (money amber, orders green, stock blue, people purple, service red) gives the
 * list a silhouette: the reader finds "the money ones" before reading a word, and urgency gets to
 * stay rare and therefore legible, carried by a thin rail and one small pill.
 */

const ICONS: Record<string, ReactNode> = {
  cart: <ShoppingCart />, ban: <Ban />, idCard: <IdCard />, undo: <Undo2 />, rupee: <Banknote />,
  bank: <Landmark />, percent: <Percent />, gift: <Gift />, boxes: <Boxes />, truckOut: <Truck />,
  truckIn: <Truck />, wallet: <Wallet />, receipt: <Receipt />, clock: <Clock />, wrench: <Wrench />,
  package: <Package />, alert: <AlertTriangle />, phone: <Phone />, shield: <ShieldAlert />,
  map: <MapIcon />, userCheck: <UserCheck />, calendar: <Calendar />, info: <Info />, check: <ClipboardCheck />,
};

/** Subject → the tile's tint. Six families, far enough apart to tell apart at a glance. */
const CATEGORY: Record<WorkCategory, string> = {
  money: 'bg-harvest-100 text-harvest-700 dark:bg-harvest-400/15 dark:text-harvest-300',
  orders: 'bg-brand-100 text-brand-700 dark:bg-brand-400/15 dark:text-brand-300',
  stock: 'bg-sky-400/15 text-sky-600 dark:text-sky-400',
  people: 'bg-plum-400/15 text-plum-600 dark:text-plum-400',
  service: 'bg-danger-500/10 text-danger-600 dark:text-danger-500',
  general: 'bg-surface-2 text-muted',
};

/**
 * The same six families again, this time as a wash across the ROW rather than a fill behind the
 * icon. A worklist is read by scanning down the left edge, and with only a 44px coloured square to
 * go on, every row was a white rectangle that had to be read word-by-word to be sorted. Bleeding
 * the subject's colour rightward out of that square — and letting it die out by 40% of the width —
 * makes the money rows, the stock rows and the service rows separable before any of them is read,
 * without putting a colour anywhere near the text it would have to sit behind.
 */
const CATEGORY_WASH: Record<WorkCategory, string> = {
  money: 'from-harvest-400/[0.16]',
  orders: 'from-brand-500/[0.11]',
  stock: 'from-sky-500/[0.11]',
  people: 'from-plum-400/[0.11]',
  service: 'from-danger-500/[0.10]',
  general: 'from-line/50',
};

/**
 * The subject's name — the one place every screen that names a subject reads it from: the /my-work
 * tiles and tabs, the row chips, and the greeting's breakdown strip.
 *
 * "Payments" rather than "money", and capitalised, because these words get printed NEXT TO A COUNT
 * OF THEIR OWN ("Orders 4") on a list whose rows carry counts of the records themselves ("say yes
 * or no to 39 orders"). A lower-case "4 orders" two lines above "39 orders" reads as a
 * contradiction; a named subject with a figure beside it reads as a breakdown, which is what it is.
 */
export const CATEGORY_LABEL: Record<WorkCategory, string> = {
  money: 'Payments', orders: 'Orders', stock: 'Stock',
  people: 'People', service: 'Service', general: 'Others',
};

/**
 * "waiting 34 days" — said in words, because a date is a subtraction the reader should not have to do.
 *
 * NOT "overdue by 34 days", however tempting: none of these rows carries a due date. This is the age
 * of the oldest thing behind the row, and calling it overdue would assert a deadline that does not
 * exist anywhere in the data.
 */
function waitedFor(days?: number): string | null {
  if (!days || days < 1) return null;
  if (days === 1) return 'waiting since yesterday';
  return `waiting ${days} days`;
}

/** Join parts into one spoken sentence, without doubling the full stops the copy already carries. */
function sentence(...parts: (string | null | undefined)[]): string {
  return parts.filter(Boolean).map((p) => String(p).replace(/\.+$/, '')).join('. ') + '.';
}

export function WorkRow({ item, onNavigate }: { item: WorkItem; onNavigate?: () => void }) {
  const navigate = useNavigate();
  const waited = waitedFor(item.oldestDays);
  const urgent = item.tone === 'urgent';

  return (
    <button
      type="button"
      onClick={() => { onNavigate?.(); navigate(item.link); }}
      // The accessible name is the whole row, so a screen reader announces the task, its
      // consequence and its size in one go rather than reading "37, button". `sentence()` joins the
      // parts because the copy already ends in full stops.
      aria-label={sentence(
        item.title,
        item.hint,
        item.amount ? `worth ${inr(item.amount)}` : null,
        waited ? `waiting ${waited}` : null,
      )}
      className={cn(
        'group relative flex w-full items-start gap-3 border-y border-r border-line bg-surface',
        'bg-linear-to-r to-40% to-transparent',
        CATEGORY_WASH[item.category] ?? CATEGORY_WASH.general,
        'px-3 py-3.5 text-left transition-colors sm:gap-4 sm:px-4',
        'hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500',
        // The rail is the ONLY thing urgency owns in the row's frame. Everything else it says, it
        // says in words.
        'border-l-4',
        urgent ? 'border-l-danger-500' : 'border-l-transparent',
      )}
    >
      {/* Subject tile. Carries the icon at every size — the count moved to the right rather than
          replacing it, so a row is identifiable by shape before it is read. */}
      <span
        className={cn('mt-0.5 grid size-11 shrink-0 place-items-center rounded-xl [&>svg]:size-5', CATEGORY[item.category] ?? CATEGORY.general)}
        aria-hidden
      >
        {ICONS[item.icon] ?? ICONS.check}
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="type-body-strong text-content">{item.title}</span>
        <span className="type-caption text-muted">{item.hint}</span>
        {waited && (
          <span
            className={cn(
              'mt-0.5 inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 type-caption-strong',
              urgent ? 'bg-danger-500/10 text-danger-600 dark:text-danger-500' : 'bg-surface-2 text-muted',
            )}
          >
            <Clock className="size-3" aria-hidden />
            {waited}
          </span>
        )}
      </span>

      {/* The size of the job, right-aligned in a fixed column so the numbers form a readable
          edge down the list instead of floating at the end of sentences of different lengths. */}
      <span className="flex shrink-0 flex-col items-end gap-0.5 pl-1 text-right">
        {item.count > 0 && <span className="type-metric-sm tabular-nums text-content">{item.count}</span>}
        {item.amount ? (
          <span className="type-caption-strong tabular-nums text-muted">{inr(item.amount)}</span>
        ) : null}
      </span>

      <ChevronRight
        className="mt-3 size-5 shrink-0 text-subtle transition-transform group-hover:translate-x-0.5"
        aria-hidden
      />
    </button>
  );
}


/**
 * The /my-work page's row — a table-style line rather than the greeting modal's washed card.
 *
 * Same data, same sentence, same destination as `WorkRow` (they share the icon and category maps
 * directly above, so the two can never disagree about what a piece of work IS). What differs is the
 * register: the modal is a short interruption, so its rows are cards that carry their own colour;
 * the page is a worklist you scan twenty rows of, so its rows are hairline-separated lines with the
 * colour concentrated in the icon plate and the chips. A page of twenty washed cards was a stack of
 * coloured slabs with the text fighting the fill.
 */
export function WorkListRow({ item, first }: { item: WorkItem; first?: boolean }) {
  const navigate = useNavigate();
  const waited = waitedFor(item.oldestDays);
  const urgent = item.tone === 'urgent';
  const mine = item.group === 'ACT';

  return (
    <button
      type="button"
      onClick={() => navigate(item.link)}
      aria-label={sentence(
        item.title,
        item.hint,
        item.amount ? `worth ${inr(item.amount)}` : null,
        waited ? waited : null,
      )}
      className={cn(
        'group flex w-full items-start gap-3 px-4 py-3.5 text-left transition-colors sm:gap-4',
        'hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500',
        !first && 'border-t border-line',
      )}
    >
      {/* The subject's colour lives here and in the chips — never behind the sentence. */}
      <span
        className={cn('mt-0.5 grid size-11 shrink-0 place-items-center rounded-xl [&>svg]:size-5', CATEGORY[item.category] ?? CATEGORY.general)}
        aria-hidden
      >
        {ICONS[item.icon] ?? ICONS.check}
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="type-body-strong text-content">{item.title}</span>
        <span className="type-caption text-muted">{item.hint}</span>
        <span className="flex flex-wrap items-center gap-1.5">
          {waited && (
            <span className={cn(
              'inline-flex items-center gap-1 rounded-pill px-2 py-0.5 type-caption-strong',
              urgent ? 'bg-danger-500/10 text-danger-600 dark:text-danger-500' : 'bg-surface-2 text-muted',
            )}>
              <Clock className="size-3" aria-hidden />
              {waited}
            </span>
          )}
          <span className="inline-flex items-center gap-1 rounded-pill bg-surface-2 px-2 py-0.5 type-caption text-muted">
            <span className={cn('size-2 rounded-pill', DOT[item.category] ?? DOT.general)} aria-hidden />
            {CATEGORY_LABEL[item.category]}
          </span>
        </span>
      </span>

      {/*
        * WHO IT IS WITH. This is the one column that would be a constant label if the page listed
        * only the reader's own queue — so the page puts BOTH groups in one list and this says which
        * is which. "Assigned to you" on every row would be the column header printed twenty times.
        */}
      <span className="hidden shrink-0 items-center gap-1.5 self-center pl-2 type-caption text-muted lg:inline-flex">
        {mine ? <UserCheck className="size-3.5" aria-hidden /> : <Hourglass className="size-3.5" aria-hidden />}
        {mine ? 'Assigned to you' : 'With someone else'}
      </span>

      {/* The size of the job, right-aligned in a fixed column so the numbers form a readable edge
          down the list instead of floating at the end of sentences of different lengths. */}
      <span className="flex w-[92px] shrink-0 flex-col items-end gap-0.5 self-center text-right">
        {item.count > 0 && <span className="type-metric-sm tabular-nums text-content">{item.count}</span>}
        {item.amount ? <span className="type-caption-strong tabular-nums text-muted">{inr(item.amount)}</span> : null}
      </span>

      <ChevronRight className="mt-3 size-5 shrink-0 text-subtle transition-transform group-hover:translate-x-0.5" aria-hidden />
    </button>
  );
}

/**
 * One titled group of rows. The line under the heading is not decoration: "waiting on someone else"
 * is a genuinely confusing idea if you have not met it before, so it is spelled out.
 */
export function WorkGroupBlock({
  title, description, items, onNavigate,
}: { title: string; description: string; items: WorkItem[]; onNavigate?: () => void }) {
  if (!items.length) return null;
  return (
    <section className="flex flex-col gap-2.5">
      <div>
        <h2 className="type-section-title text-content">{title}</h2>
        <p className="type-caption text-muted">{description}</p>
      </div>
      <div className="overflow-hidden rounded-card border-line shadow-soft [&>button:first-child]:rounded-t-card [&>button:last-child]:rounded-b-card [&>button+button]:border-t-0">
        {items.map((i) => <WorkRow key={i.key} item={i} onNavigate={onNavigate} />)}
      </div>
    </section>
  );
}

/**
 * The headline over a list: how much is waiting, and what it is made of.
 *
 * The money line is the single most informative thing on the screen for anyone who approves: it is
 * the difference between a queue of twenty trivial items and a queue holding up two lakh rupees,
 * which a count alone can never tell you.
 *
 * EVERY SUBJECT IS SHOWN — never a top-N slice. The strip used to cap at three chips, which quietly
 * broke the only check a reader can perform on it: the chips are a PARTITION of the list, so they
 * have to add up to the count in the sentence directly above them. Capped at three they added to
 * fifteen out of nineteen, with nothing on screen admitting that four subjects were missing — so
 * the figures read as wrong rather than as abridged. There are only ever six subjects, and on a
 * screen narrow enough to matter they wrap to a second line, which costs nothing.
 *
 * EVERY CHIP DRILLS. A figure the reader can see is a figure they can open — and a chip is the only
 * place the app ever states "six of these are payments", so being unable to press it means the
 * reader has to go to /my-work and rebuild that slice by hand from a filter row. `hrefFor` gets the
 * subject, or `null` for the money total, and returns the address of the list that holds exactly
 * that: the chips stay a summary, and the summary becomes the way in.
 */
export function WorkSummary({ items, className, hrefFor, onNavigate }: {
  items: WorkItem[];
  className?: string;
  /**
   * Provide to make every chip a LINK to the list it counts — `null` is the money total, which is a
   * different slice from the "Payments" subject (money cuts ACROSS subjects: an order carrying an
   * amount is in it). Omit and the chips render as plain read-only counts.
   */
  hrefFor?: (cat: WorkCategory | null) => string;
  /** Fired just before a chip navigates — the login greeting uses it to close itself. */
  onNavigate?: () => void;
}) {
  const money = items.reduce((sum, i) => sum + (i.amount ?? 0), 0);
  const byCat = new Map<WorkCategory, number>();
  for (const i of items) byCat.set(i.category, (byCat.get(i.category) ?? 0) + 1);
  // Biggest subject first: the strip reads as a ranking, so the eye finds where the bulk of the
  // work sits without having to read every chip.
  const chips = [...byCat.entries()].sort((a, b) => b[1] - a[1]);
  if (!items.length) return null;

  return (
    <div className={cn('flex flex-wrap items-center gap-x-2 gap-y-1.5', className)}>
      {/*
        The money total is the one number here you cannot get by looking at the list — the count of
        rows is visible, ₹79,448 is six additions. It was left a plain <span> on the argument that a
        total has nothing to filter down to, which was wrong: it totals a REAL SUBSET (every row
        carrying an amount), the list page has always had that exact slice, and a rupee figure is the
        chip an approver most wants to open. It now goes there.
      */}
      {money > 0 && (
        <Chip
          href={hrefFor?.(null)}
          onNavigate={onNavigate}
          label={`${inr(money)} waiting — open everything with money on it`}
          className={cn(
            'bg-harvest-100 type-caption-strong text-harvest-700 dark:bg-harvest-400/15 dark:text-harvest-300',
            hrefFor && 'hover:bg-harvest-200 dark:hover:bg-harvest-400/25',
          )}
        >
          <Banknote className="size-3.5" aria-hidden />
          <span aria-hidden>{inr(money)} waiting</span>
        </Chip>
      )}

      {/*
        The subject chips are a BREAKDOWN, and are written as one: the subject NAMED, its figure
        beside it. Written the other way round — "4 orders" — a chip asserts a quantity of RECORDS,
        and on this list that assertion is wrong twice over: those are four order TASKS, one of
        which is itself "say yes or no to 39 orders". Naming the subject first keeps each chip a
        legend for the list rather than a rival count of it, and keeps the figures addable to the
        headline directly above them.
      */}
      {chips.map(([cat, n]) => (
        <Chip
          key={cat}
          href={hrefFor?.(cat)}
          onNavigate={onNavigate}
          // Spoken as a sentence, because "Orders 4" read aloud is two loose nouns.
          label={`${CATEGORY_LABEL[cat]}: ${n} ${n === 1 ? 'task' : 'tasks'}`}
          className={cn('bg-surface-2 type-caption text-muted', hrefFor && 'hover:bg-surface-3 hover:text-content')}
        >
          <span className={cn('size-2 rounded-full', DOT[cat] ?? DOT.general)} aria-hidden />
          <span aria-hidden>{CATEGORY_LABEL[cat]}</span>
          <span aria-hidden className="type-caption-strong tabular-nums text-content">{n}</span>
        </Chip>
      ))}
    </div>
  );
}

/**
 * One chip of the summary strip — a link when it has somewhere to go, a plain span when it does not.
 *
 * Both render the SAME pill, because a chip that grew a border or a chevron the moment it became
 * pressable would make the strip look like two kinds of thing. What marks it as pressable is the
 * hover the caller passes in, the pointer cursor, and the focus ring — all of which cost nothing
 * when the chip is inert.
 *
 * The visible content is `aria-hidden` and the accessible name comes from `label`, so a screen
 * reader hears "Orders: 4 tasks" rather than the three loose tokens the layout is made of.
 */
function Chip({ href, onNavigate, label, className, children }: {
  href?: string;
  onNavigate?: () => void;
  label: string;
  className?: string;
  children: ReactNode;
}) {
  const shape = cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1', className);
  if (!href) return <span aria-label={label} className={shape}>{children}</span>;
  return (
    <Link
      to={href}
      aria-label={label}
      onClick={onNavigate}
      className={cn(shape, 'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500')}
    >
      {children}
    </Link>
  );
}

/** The chip dot, matching each subject tile so the strip and the rows read as one colour system.
 *  Exported so /my-work's own category chips cannot drift from the rows they filter. */
export const DOT: Record<WorkCategory, string> = {
  money: 'bg-harvest-400', orders: 'bg-brand-500', stock: 'bg-sky-500',
  people: 'bg-plum-500', service: 'bg-danger-500', general: 'bg-line-strong',
};

/**
 * "Nothing is waiting for you."
 *
 * A positive statement with a tick, not a grey "No data". An empty work list is the GOAL state of
 * this screen, and for a nervous user it matters that it looks like success rather than like
 * something failed to load.
 */
export function WorkAllClear({ compact }: { compact?: boolean }) {
  return (
    <div className={cn('flex flex-col items-center gap-2 text-center', compact ? 'py-8' : 'py-16')}>
      <span className="grid size-14 place-items-center rounded-full bg-success-500/10 text-success-600 [&>svg]:size-7">
        <CheckCircle2 />
      </span>
      <p className="type-body-strong text-content">Nothing is waiting for you</p>
      <p className="type-body text-muted">You are all caught up. Well done.</p>
    </div>
  );
}
